/**
 * Worker'dan gelen hataları arayüzün ayırt edebileceği hâle getirir.
 * Sözleşmedeki `RpcError.code` değerleri kullanıcıya farklı ekran gösterir —
 * özellikle `LOCKED_BY_OTHER_TAB` (Aşama 0'da ölçülen SAHPool tek-sekme kilidi).
 */

import { motorMesajiMi, type RpcError } from "@parafon/contracts/local-api";
import { motorMetni } from "../dil/motorMetni";
import { DIL, M } from "../dil";

const CODES = new Set<RpcError["code"]>([
  "LOCKED_BY_OTHER_TAB",
  "OPFS_UNAVAILABLE",
  "MIGRATION_FAILED",
  "NOT_FOUND",
  "VALIDATION",
  "TIMEOUT",
  "SERVER_UNAVAILABLE",
  "KILITLI",
  "KURULUM_GEREKLI",
  "DEFTER_KAYIP",
  "BASKA_DEFTER",
  "UNKNOWN",
]);

export function isRpcError(value: unknown): value is RpcError {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return typeof v["message"] === "string" && typeof v["code"] === "string" && CODES.has(v["code"] as RpcError["code"]);
}

/** Bir Error nesnesinin içine sarılmış RpcError'ı da yakalar. */
export function asRpcError(value: unknown): RpcError | null {
  if (isRpcError(value)) return value;
  if (value instanceof Error) {
    const cause = (value as Error & { cause?: unknown }).cause;
    if (isRpcError(cause)) return cause;
    const code = (value as Error & { code?: unknown }).code;
    if (typeof code === "string" && CODES.has(code as RpcError["code"])) {
      return { message: value.message, code: code as RpcError["code"] };
    }
  }
  return null;
}

export function isLockedByOtherTab(value: unknown): boolean {
  return asRpcError(value)?.code === "LOCKED_BY_OTHER_TAB";
}

/** Uygulama dosyası sunucudan alınamadı — veritabanına dokunulmadı. */
/** Geri yüklenmek istenen yedek başka bir deftere ait (D6'ya yönlendirilir). */
export function isBaskaDefter(value: unknown): boolean {
  return asRpcError(value)?.code === "BASKA_DEFTER";
}

export function isServerUnavailable(value: unknown): boolean {
  return asRpcError(value)?.code === "SERVER_UNAVAILABLE";
}

/** Bir hatanın kullanıcıya gösterilecek metni. Teknik detay ayrı alanda. */
/**
 * Motorun KOD olarak verdiği sebep (sanal hesap üretimi durdu, ayrıntı
 * "sanal:uretim"): ileti alanındaki mesaj dilin sözlüğünden cümleye çevrilir.
 */
function motorIletisi(rpc: RpcError): string | null {
  if (rpc.detail?.startsWith("sanal:uretim") !== true) return null;
  try {
    const m: unknown = JSON.parse(rpc.message);
    return motorMesajiMi(m) ? motorMetni(m) : null;
  } catch {
    return null;
  }
}

/**
 * MOTORUN TEKNİK METNİ (kullanıcı kararı 03.11.2026, seçenek b): motorun ve
 * merkez istemcisinin ham hata iletileri Türkçe yazılmıştır. Türkçe ekranda
 * eskisi gibi gösterilir; başka dilde ekranda hata TÜRÜNÜN sözlükteki cümlesi
 * görünür, ham ileti yalnızca "teknik ayrıntı"ya (`errorDetail`) iner.
 */
const HAM_ILETI_GOSTERILIR: boolean = DIL === "tr";

function hamIleti(rpc: RpcError): string | null {
  if (rpc.message === "") return null;
  return HAM_ILETI_GOSTERILIR ? rpc.message : null;
}

export function errorText(value: unknown): string {
  const rpc = asRpcError(value);
  if (rpc) {
    switch (rpc.code) {
      case "LOCKED_BY_OTHER_TAB":
        return M.kabuk.hata.LOCKED_BY_OTHER_TAB;
      case "OPFS_UNAVAILABLE":
        return M.kabuk.hata.OPFS_UNAVAILABLE;
      case "MIGRATION_FAILED":
        return M.kabuk.hata.MIGRATION_FAILED;
      case "NOT_FOUND":
        return M.kabuk.hata.NOT_FOUND;
      case "VALIDATION":
        return motorIletisi(rpc) ?? hamIleti(rpc) ?? M.kabuk.hata.VALIDATION;
      case "TIMEOUT":
        return hamIleti(rpc) ?? M.kabuk.hata.TIMEOUT;
      // Sebebi (ağ, HTTP durumu, HTML) yoklama söyler; metin ondan gelir.
      case "SERVER_UNAVAILABLE":
        return hamIleti(rpc) ?? M.kabuk.hata.SERVER_UNAVAILABLE;
      case "KILITLI":
        return M.kabuk.hata.KILITLI;
      case "KURULUM_GEREKLI":
        return M.kabuk.hata.KURULUM_GEREKLI;
      case "DEFTER_KAYIP":
        return hamIleti(rpc) ?? M.kabuk.hata.DEFTER_KAYIP;
      case "BASKA_DEFTER":
        return M.kabuk.hata.BASKA_DEFTER;
      case "UNKNOWN":
        return hamIleti(rpc) ?? M.kabuk.hata.UNKNOWN;
    }
  }
  if (value instanceof Error) return value.message;
  return String(value);
}

export function errorDetail(value: unknown): string | undefined {
  const rpc = asRpcError(value);
  // Ekranda gösterilmeyen ham ileti teknik ayrıntıda kalır.
  if (rpc !== null && !HAM_ILETI_GOSTERILIR && rpc.message !== "" && motorIletisi(rpc) === null) {
    return rpc.detail === undefined ? rpc.message : `${rpc.message}\n${rpc.detail}`;
  }
  if (rpc?.detail !== undefined) return rpc.detail;
  if (value instanceof Error && value.stack !== undefined) return value.stack;
  return undefined;
}
